import { useEffect, useRef, useState } from "react";
import { mesCurto } from "../lib/perito/agenda";
import { formatBRL } from "../lib/perito/format";
import type { FaixaCalculada, MesDaSerie } from "../lib/perito/financeiro";

// Os gráficos da Central do Perito (fase B). Lá eram do recharts, que o hub não tem; aqui são SVG no tamanho
// REAL (a largura é medida, como nos gráficos das Avaliações), para o texto do eixo não encolher no celular.
// Nenhum faz conta: a série e as faixas chegam prontas de `lib/perito/financeiro.ts`. E todo gráfico tem os
// mesmos números em texto, numa tabela dentro de um `<details>`: a barra é para o olho, a tabela é para quem
// lê a tela com leitor ou quer o valor exato.

function useLargura<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [largura, setLargura] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setLargura(Math.floor(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, largura];
}

/** "R$ 1,5 mil", "R$ 800" — o rótulo curto do eixo. */
function reaisCurtos(v: number): string {
  if (Math.abs(v) >= 1000) return `R$ ${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`;
  return `R$ ${Math.round(v).toLocaleString("pt-BR")}`;
}

/**
 * As marcas do eixo: um PASSO redondo (1, 2, 2,5 ou 5 vezes uma potência de 10) para umas quatro divisões, e o
 * teto no primeiro múltiplo dele que cobre o máximo. A primeira versão dividia um teto redondo em quatro, e o eixo
 * de R$ 5 mil saía com "R$ 1,3 mil" e "R$ 3,8 mil" (a foto achou).
 */
function marcasDoEixo(maximo: number): number[] {
  const alvo = maximo > 0 ? maximo / 4 : 250;
  const p = 10 ** Math.floor(Math.log10(alvo));
  const passo = [1, 2, 2.5, 5, 10].map((k) => k * p).find((v) => v >= alvo) ?? 10 * p;
  const n = Math.max(1, Math.ceil(maximo / passo));
  return Array.from({ length: n + 1 }, (_, i) => i * passo);
}

/**
 * Os honorários por mês: o PREVISTO (o que vence no mês) e o RECEBIDO (o que entrou no mês), lado a lado. As
 * duas cores são as séries 1 e 3 da casa, e a posição também separa: o previsto sempre à esquerda.
 */
export function BarrasPorMes({ serie, altura = 220 }: { serie: MesDaSerie[]; altura?: number }) {
  const [ref, W] = useLargura<HTMLDivElement>();
  const ESQ = 72, DIR = 8, TOPO = 10, BASE = 26;
  const ticks = marcasDoEixo(Math.max(...serie.map((m) => Math.max(m.previsto, m.recebido))));
  const teto = ticks[ticks.length - 1];
  const largura = Math.max(W, 280);
  const areaW = largura - ESQ - DIR;
  const areaH = altura - TOPO - BASE;
  const grupo = areaW / serie.length;
  const barra = Math.max(4, Math.min(28, grupo * 0.32));
  const y = (v: number) => TOPO + areaH - (v / teto) * areaH;

  return (
    <div data-barras-por-mes>
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-cinza">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="size-2.5 rounded-sm bg-serie-1" />Previsto (vence no mês)</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="size-2.5 rounded-sm bg-serie-3" />Recebido (entrou no mês)</span>
      </div>
      <div ref={ref} className="w-full">
        {W > 0 && (
          <svg width={largura} height={altura} role="img" aria-label="Honorários previstos e recebidos por mês. Os números estão na tabela abaixo.">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={ESQ} x2={largura - DIR} y1={y(t)} y2={y(t)} className="stroke-borda" strokeWidth={1} />
                <text x={ESQ - 8} y={y(t)} textAnchor="end" dominantBaseline="middle" className="fill-cinza text-[11px] tabular-nums">{reaisCurtos(t)}</text>
              </g>
            ))}
            {serie.map((m, i) => {
              const x0 = ESQ + i * grupo + grupo / 2;
              return (
                <g key={m.mes} data-mes={m.mes} data-previsto={m.previsto} data-recebido={m.recebido}>
                  <rect x={x0 - barra - 2} width={barra} y={y(m.previsto)} height={Math.max(0, TOPO + areaH - y(m.previsto))} rx={3} className="fill-serie-1">
                    <title>{`${mesCurto(m.mes)}: previsto ${formatBRL(m.previsto)}`}</title>
                  </rect>
                  <rect x={x0 + 2} width={barra} y={y(m.recebido)} height={Math.max(0, TOPO + areaH - y(m.recebido))} rx={3} className="fill-serie-3">
                    <title>{`${mesCurto(m.mes)}: recebido ${formatBRL(m.recebido)}`}</title>
                  </rect>
                  <text x={x0} y={altura - 8} textAnchor="middle" className="fill-cinza text-[11px]">{mesCurto(m.mes)}</text>
                </g>
              );
            })}
          </svg>
        )}
      </div>
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer font-semibold text-link">Ver os números</summary>
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold tracking-[0.12em] text-cinza uppercase">
              <th className="py-1.5">Mês</th><th className="py-1.5 text-right">Previsto</th><th className="py-1.5 text-right">Recebido</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {serie.map((m) => (
              <tr key={m.mes}>
                <td className="py-1.5">{mesCurto(m.mes)}/{m.mes.slice(2, 4)}</td>
                <td className="py-1.5 text-right tabular-nums">{formatBRL(m.previsto)}</td>
                <td className="py-1.5 text-right tabular-nums">{formatBRL(m.recebido)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

/**
 * As perícias NOMEADAS e CONCLUÍDAS por mês (fase C), lado a lado, como as barras do dinheiro: as cores são as
 * séries 1 e 3, e a posição também separa. O eixo é de contagem, com passo inteiro. Lá era um gráfico de linhas;
 * contagem de um mês é quantidade, e quantidade é barra (o eixo começa no zero).
 */
export function ContagemPorMes({ meses, altura = 220 }: { meses: Array<{ mes: string; nomeadas: number; concluidas: number }>; altura?: number }) {
  const [ref, W] = useLargura<HTMLDivElement>();
  const ESQ = 36, DIR = 8, TOPO = 10, BASE = 26;
  const maximo = Math.max(...meses.map((m) => Math.max(m.nomeadas, m.concluidas)));
  const ticks = marcasDoEixo(Math.max(4, maximo)).filter((t) => Number.isInteger(t));
  const teto = ticks[ticks.length - 1];
  const largura = Math.max(W, 280);
  const areaW = largura - ESQ - DIR;
  const areaH = altura - TOPO - BASE;
  const grupo = areaW / meses.length;
  const barra = Math.max(4, Math.min(28, grupo * 0.32));
  const y = (v: number) => TOPO + areaH - (v / teto) * areaH;

  return (
    <div data-contagem-por-mes>
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-cinza">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="size-2.5 rounded-sm bg-serie-1" />Nomeadas</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="size-2.5 rounded-sm bg-serie-3" />Concluídas</span>
      </div>
      <div ref={ref} className="w-full">
        {W > 0 && (
          <svg width={largura} height={altura} role="img" aria-label="Perícias nomeadas e concluídas por mês. Os números estão na tabela abaixo.">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={ESQ} x2={largura - DIR} y1={y(t)} y2={y(t)} className="stroke-borda" strokeWidth={1} />
                <text x={ESQ - 8} y={y(t)} textAnchor="end" dominantBaseline="middle" className="fill-cinza text-[11px] tabular-nums">{t}</text>
              </g>
            ))}
            {meses.map((m, i) => {
              const x0 = ESQ + i * grupo + grupo / 2;
              return (
                <g key={m.mes} data-mes={m.mes} data-nomeadas={m.nomeadas} data-concluidas={m.concluidas}>
                  <rect x={x0 - barra - 2} width={barra} y={y(m.nomeadas)} height={Math.max(0, TOPO + areaH - y(m.nomeadas))} rx={3} className="fill-serie-1">
                    <title>{`${mesCurto(m.mes)}: ${m.nomeadas} nomeada(s)`}</title>
                  </rect>
                  <rect x={x0 + 2} width={barra} y={y(m.concluidas)} height={Math.max(0, TOPO + areaH - y(m.concluidas))} rx={3} className="fill-serie-3">
                    <title>{`${mesCurto(m.mes)}: ${m.concluidas} concluída(s)`}</title>
                  </rect>
                  <text x={x0} y={altura - 8} textAnchor="middle" className="fill-cinza text-[11px]">{mesCurto(m.mes)}</text>
                </g>
              );
            })}
          </svg>
        )}
      </div>
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer font-semibold text-link">Ver os números</summary>
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold tracking-[0.12em] text-cinza uppercase">
              <th className="py-1.5">Mês</th><th className="py-1.5 text-right">Nomeadas</th><th className="py-1.5 text-right">Concluídas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {meses.map((m) => (
              <tr key={m.mes}>
                <td className="py-1.5">{mesCurto(m.mes)}/{m.mes.slice(2, 4)}</td>
                <td className="py-1.5 text-right tabular-nums">{m.nomeadas}</td>
                <td className="py-1.5 text-right tabular-nums">{m.concluidas}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

/**
 * O ranking em barras horizontais, com o nome inteiro (lá era a regra para rótulo longo, como o tipo de
 * perícia). Mostra todas as linhas: cortar no top-N esconderia informação sem avisar.
 */
export function Ranking({ itens, formato = (v) => v.toLocaleString("pt-BR") }: { itens: Array<{ nome: string; valor: number; detalhe?: string }>; formato?: (v: number) => string }) {
  const maximo = Math.max(1, ...itens.map((i) => i.valor));
  return (
    <ol className="flex flex-col gap-3" data-ranking>
      {itens.map((i) => (
        <li key={i.nome} className="min-w-0">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-tinta" title={i.nome}>{i.nome}</span>
            <span className="shrink-0 font-semibold text-titulo tabular-nums">
              {formato(i.valor)}
              {i.detalhe && <span className="ml-1.5 text-xs font-normal text-cinza">{i.detalhe}</span>}
            </span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-chip">
            <div className="h-full rounded-full bg-serie-1" style={{ width: `${Math.max(4, (i.valor / maximo) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Onde está o dinheiro: UMA barra fatiada na proporção de cada faixa do fluxo judicial (como lá). É ela que
 * mostra onde o dinheiro está parado, o que cinco números lado a lado não mostram. A porcentagem de cada faixa
 * vai escrita na legenda.
 */
export function BarraDeComposicao({ faixas }: { faixas: FaixaCalculada[] }) {
  return (
    <div data-composicao>
      <div role="img" aria-label={faixas.filter((f) => f.pct > 0).map((f) => `${f.rotulo}: ${f.pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`).join(", ")}
        className="flex h-4 w-full gap-0.5 overflow-hidden rounded-full bg-chip">
        {faixas.map((f) => (f.pct > 0 ? <div key={f.id} className={`h-full ${f.cor}`} style={{ width: `${f.pct}%` }} title={`${f.rotulo}: ${formatBRL(f.valor)}`} /> : null))}
      </div>
      <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {faixas.map((f) => (
          <li key={f.id} className="flex items-center gap-2 text-sm" data-faixa={f.id} data-pct={f.pct.toFixed(1)}>
            <span aria-hidden="true" className={`size-2.5 shrink-0 rounded-full ${f.cor}`} />
            <span className="truncate text-tinta">{f.rotulo}</span>
            <span className="ml-auto font-semibold text-titulo tabular-nums">{f.pct.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
